import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Lock, CheckCircle, PlayCircle, BookOpen, Zap, MessageCircle, MessageSquare } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { translations } from '../../translations';
import { getMyProgress, getMyNotifications } from '../../services/studioApi';
import UnifiedHeader from '../../components/UnifiedHeader';
import './Studio.css';

function MasteryRing({ score }) {
  const pct = Math.round(score * 100);
  const r = 20;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  const color = pct >= 80 ? '#4caf50' : pct >= 50 ? '#667eea' : pct > 0 ? '#ff9800' : '#e0e0e0';

  return (
    <div className="mastery-ring">
      <svg width="52" height="52" viewBox="0 0 52 52">
        <circle cx="26" cy="26" r={r} fill="none" stroke="#e8ecf0" strokeWidth="5" />
        <circle cx="26" cy="26" r={r} fill="none" stroke={color} strokeWidth="5"
          strokeDasharray={`${dash} ${circ - dash}`} strokeLinecap="round" />
      </svg>
      <span className="mastery-ring-text">{pct}%</span>
    </div>
  );
}

export default function StudentCourseView() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = (translations[language]?.studio) || {};
  const tStudent = t.student || {};

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [conceptNotifs, setConceptNotifs] = useState({});

  useEffect(() => {
    getMyProgress(courseId)
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [courseId]);

  const refreshNotifs = useCallback(() => {
    getMyNotifications()
      .then(d => {
        const map = {};
        (d.concepts || []).forEach(n => { map[n.concept_id] = n; });
        setConceptNotifs(map);
      })
      .catch(console.error);
  }, []);

  useEffect(() => { refreshNotifs(); }, [refreshNotifs]);

  const isLocked = (concept, allConcepts) => {
    if (!concept.prerequisites?.length) return false;
    return concept.prerequisites.some(prereqId => {
      const prereq = allConcepts.find(c => c.id === prereqId);
      return prereq && (prereq.mastery_score || 0) < 0.6;
    });
  };

  const statusClass = (concept, locked) => {
    if (locked) return 'locked';
    if ((concept.mastery_score || 0) >= 0.8) return 'mastered';
    if ((concept.mastery_score || 0) > 0) return 'in-progress';
    return '';
  };

  if (loading) return <div className="studio-page"><UnifiedHeader /><div className="loading-center"><div className="spinner" /></div></div>;

  const concepts = data?.concepts || [];
  const dueSoon = (data?.assignments || []).filter(a =>
    !a.submission_id &&
    a.due_date && new Date(a.due_date) > new Date() &&
    new Date(a.due_date) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
  );
  const practiceNeeded = concepts.filter(c => (c.mastery_score || 0) < 0.6 && (c.mastery_score || 0) > 0);

  return (
    <div className="studio-page">
      <UnifiedHeader subRow={
        <div className="studio-subheader-left">
          <button className="studio-back-btn" onClick={() => navigate('/studio/student')}>
            <ArrowLeft size={15} /> {t.back || 'Back'}
          </button>
          <h1 className="studio-page-title">{tStudent.courseProgress || 'Course Progress'}</h1>
        </div>
      } />

      <div className="studio-main">
        {/* Due soon & practice suggestions */}
        {(dueSoon.length > 0 || practiceNeeded.length > 0) && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
            {dueSoon.length > 0 && (
              <div className="studio-card" style={{ borderLeft: '4px solid #ff9800' }}>
                <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.9rem', fontWeight: 700, color: '#e65100' }}>
                  {tStudent.dueSoon || 'Due Soon'}
                </h3>
                {dueSoon.slice(0, 3).map(a => (
                  <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0', borderBottom: '1px solid #f5f5f5' }}>
                    <span style={{ fontSize: '0.85rem', fontWeight: 500, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.title}</span>
                    <button className="btn-primary" style={{ padding: '0.3rem 0.75rem', fontSize: '0.78rem', flexShrink: 0, whiteSpace: 'nowrap' }}
                      onClick={() => navigate(`/studio/student/assignments/${a.id}`)}>
                      {tStudent.start || 'Start'}
                    </button>
                  </div>
                ))}
              </div>
            )}
            {practiceNeeded.length > 0 && (
              <div className="studio-card" style={{ borderLeft: '4px solid #667eea' }}>
                <h3 style={{ margin: '0 0 0.75rem', fontSize: '0.9rem', fontWeight: 700, color: '#4a3f9f' }}>
                  <Zap size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  {tStudent.keepPracticing || 'Keep Practicing'}
                </h3>
                {practiceNeeded.slice(0, 3).map(c => (
                  <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.4rem 0', borderBottom: '1px solid #f5f5f5' }}>
                    <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>{c.title}</span>
                    <span style={{ fontSize: '0.78rem', color: '#ff9800', fontWeight: 600 }}>
                      {Math.round((c.mastery_score || 0) * 100)}%
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Concept roadmap */}
        <div className="section-header">
          <h2><BookOpen size={18} />{tStudent.concepts || 'Learning Path'}</h2>
        </div>

        {concepts.length === 0 ? (
          <div className="studio-card">
            <p style={{ color: '#aaa', textAlign: 'center', padding: '2rem 0' }}>
              {tStudent.noConceptsYet || 'Concepts are being prepared by your teacher'}
            </p>
          </div>
        ) : (
          <div className="studio-card-grid">
            {concepts.map((c, i) => {
              const locked = isLocked(c, concepts);
              const cls = statusClass(c, locked);
              return (
                <div
                  key={c.id}
                  className={`concept-card ${cls}`}
                  onClick={() => !locked && navigate(`/studio/student/concepts/${c.id}?course=${courseId}`)}
                >
                  {locked && (
                    <div style={{ position: 'absolute', top: '0.75rem', right: '0.75rem' }}>
                      <Lock size={16} color="#bbb" />
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.6rem' }}>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#bbb', textTransform: 'uppercase', letterSpacing: 1 }}>
                      {tStudent.concept || 'Concept'} {i + 1}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                      {(() => {
                        const notif = conceptNotifs[c.id] || {};
                        const unread = notif.unread || 0;
                        const hasMessages = (notif.total || 0) > 0;
                        return !locked && hasMessages && (
                          <div
                            style={{ position: 'relative', cursor: 'pointer', padding: '2px' }}
                            onClick={e => { e.stopPropagation(); navigate(`/studio/student/concepts/${c.id}?course=${courseId}&openChat=1`); }}
                            title={hasMessages ? `${notif.total} message${notif.total > 1 ? 's' : ''}` : 'Ask your teacher'}
                          >
                            <MessageCircle size={16} color={unread > 0 ? '#667eea' : hasMessages ? '#a78bfa' : '#ccc'} />
                            {unread > 0 && (
                              <span style={{
                                position: 'absolute', top: -3, right: -3,
                                background: '#ef4444', color: '#fff',
                                borderRadius: '50%', minWidth: 14, height: 14,
                                padding: '0 2px',
                                fontSize: '0.58rem', fontWeight: 700,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                lineHeight: 1, boxSizing: 'border-box',
                              }}>{unread > 9 ? '9+' : unread}</span>
                            )}
                          </div>
                        );
                      })()}
                      <MasteryRing score={c.mastery_score || 0} />
                    </div>
                  </div>
                  <h3 className="concept-card-title">{c.title}</h3>
                  {c.summary && <p className="concept-card-summary">{c.summary}</p>}
                  <div className="concept-card-footer">
                    {c.video_watched ? (
                      <span className="badge badge-green"><PlayCircle size={11} /> Watched</span>
                    ) : c.video_url ? (
                      <span className="badge badge-blue"><PlayCircle size={11} /> Video</span>
                    ) : null}
                    {(c.mastery_score || 0) >= 0.8 ? (
                      <span className="badge badge-green"><CheckCircle size={11} /> {tStudent.mastered || 'Mastered'}</span>
                    ) : locked ? (
                      <span className="badge badge-grey"><Lock size={11} /> {tStudent.locked || 'Locked'}</span>
                    ) : (
                      <button className="btn-secondary" style={{ padding: '0.25rem 0.65rem', fontSize: '0.78rem' }}>
                        {tStudent.study || 'Study'} →
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pending assignments */}
        {(data?.assignments || []).filter(a => !a.submission_id).length > 0 && (
          <>
            <div className="section-header" style={{ marginTop: '2rem' }}>
              <h2><CheckCircle size={18} />{tStudent.assignments || 'Assignments'}</h2>
            </div>
            {data.assignments.filter(a => !a.submission_id).map(a => (
              <div key={a.id} className="assignment-card">
                <div className="assignment-card-info">
                  <div className="assignment-card-title">{a.title}</div>
                  <div className="assignment-card-meta">
                    {a.due_date && <span>{tStudent.due || 'Due'}: {new Date(a.due_date).toLocaleDateString()}</span>}
                    {a.time_limit_minutes && <span>{a.time_limit_minutes}min</span>}
                  </div>
                </div>
                <button className="btn-primary" onClick={() => navigate(`/studio/student/assignments/${a.id}`)}>
                  {tStudent.start || 'Start'}
                </button>
              </div>
            ))}
          </>
        )}

        {/* All completed submissions (every attempt) */}
        {(data?.all_submissions || []).length > 0 && (
          <>
            <div className="section-header" style={{ marginTop: '2rem' }}>
              <h2><CheckCircle size={18} style={{ color: '#4caf50' }} />{tStudent.completedAssignments || 'Completed Assignments'}</h2>
            </div>
            {data.all_submissions.map(s => {
              const pct = s.max_score > 0 ? Math.round((s.total_score / s.max_score) * 100) : 0;
              return (
                <div key={s.submission_id} className="assignment-card">
                  <div className="assignment-card-info">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <div className="assignment-card-title">{s.assignment_title}</div>
                      {s.teacher_comment && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.2rem', background: '#ede9fe', color: '#5B35A0', borderRadius: 8, padding: '1px 7px', fontSize: '0.72rem', fontWeight: 700 }}>
                          <MessageSquare size={10} /> Feedback
                        </span>
                      )}
                    </div>
                    <div className="assignment-card-meta">
                      {s.submitted_at && <span>{tStudent.submittedOn || 'Submitted'}: {new Date(s.submitted_at).toLocaleDateString()}</span>}
                      {s.attempt_number > 1 && <span style={{ color: '#888' }}>#{s.attempt_number}</span>}
                      {s.time_limit_minutes && <span>{s.time_limit_minutes}min</span>}
                      <span style={{ color: pct >= 70 ? '#4caf50' : pct >= 40 ? '#ff9800' : '#f44336', fontWeight: 700 }}>
                        ✓ {pct}%
                      </span>
                    </div>
                  </div>
                  <button className="btn-secondary" onClick={() => navigate(`/studio/student/assignments/${s.assignment_id}?submission=${s.submission_id}`)}>
                    {tStudent.review || 'Review'}
                  </button>
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}
